import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { hashPassword } from '../utils/crypto';

export interface VIPUser {
  id: string;
  name: string;
  email: string;
  whatsapp?: string;
  is_active: boolean;
}

interface VIPAuthContextType {
  vipUser: VIPUser | null;
  loading: boolean;
  loginVIP: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  registerVIP: (name: string, email: string, password: string, whatsapp?: string) => Promise<{ success: boolean; error?: string }>;
  logoutVIP: () => void;
}

const VIPAuthContext = createContext<VIPAuthContextType>({
  vipUser: null,
  loading: true,
  loginVIP: async () => ({ success: false }),
  registerVIP: async () => ({ success: false }),
  logoutVIP: () => {},
});

const STORAGE_KEY = 'bk_vip_user_session_v1';

export const VIPAuthProvider = ({ children }: { children: ReactNode }) => {
  const [vipUser, setVipUser] = useState<VIPUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initSession = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as VIPUser;
          // Verificar se o usuário continua ativo no banco de dados
          const { data, error } = await supabase
            .from('vip_users')
            .select('id, name, email, whatsapp, is_active')
            .eq('id', parsed.id)
            .single();

          if (data && !error && data.is_active) {
            setVipUser(data);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            setVipUser(null);
          }
        }
      } catch (err) {
        console.error('Erro ao restaurar sessão VIP:', err);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    initSession();
  }, []);

  const loginVIP = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const hashedInput = await hashPassword(password);
      
      const { data, error } = await supabase
        .from('vip_users')
        .select('id, name, email, whatsapp, password_hash, is_active')
        .eq('email', email.trim().toLowerCase())
        .single();

      if (error || !data) {
        return { success: false, error: 'E-mail ou senha incorretos.' };
      }

      if (!data.is_active) {
        return { success: false, error: 'Este cadastro está inativo. Entre em contato com a administração.' };
      }

      if (data.password_hash !== hashedInput) {
        return { success: false, error: 'E-mail ou senha incorretos.' };
      }

      const userData: VIPUser = {
        id: data.id,
        name: data.name,
        email: data.email,
        whatsapp: data.whatsapp || undefined,
        is_active: data.is_active,
      };

      setVipUser(userData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      return { success: true };
    } catch (err) {
      console.error('Erro no login VIP:', err);
      return { success: false, error: 'Ocorreu um erro ao processar o login. Tente novamente.' };
    }
  };

  const registerVIP = async (
    name: string,
    email: string,
    password: string,
    whatsapp?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanName = name.trim();
      const cleanWhatsapp = whatsapp ? whatsapp.trim() : '';

      if (!cleanName || !cleanEmail || !password) {
        return { success: false, error: 'Preencha todos os campos obrigatórios.' };
      }

      // Verificar se o e-mail já existe
      const { data: existing } = await supabase
        .from('vip_users')
        .select('id')
        .eq('email', cleanEmail)
        .maybeSingle();

      if (existing) {
        return { success: false, error: 'Este e-mail já possui um cadastro VIP. Faça login ou solicite suporte.' };
      }

      const hashedPassword = await hashPassword(password);

      const payload: any = {
        name: cleanName,
        email: cleanEmail,
        password_hash: hashedPassword,
        is_active: true,
      };

      if (cleanWhatsapp) {
        payload.whatsapp = cleanWhatsapp;
      }

      let insertRes = await supabase
        .from('vip_users')
        .insert([payload])
        .select('id, name, email, is_active')
        .single();

      // Se a coluna whatsapp ainda não existir no banco, tenta sem a propriedade
      if (insertRes.error && (insertRes.error.message?.includes('whatsapp') || insertRes.error.code === 'PGRST204')) {
        delete payload.whatsapp;
        insertRes = await supabase
          .from('vip_users')
          .insert([payload])
          .select('id, name, email, is_active')
          .single();
      }

      const { data, error } = insertRes;

      if (error || !data) {
        if (error?.code === '23505') {
          return { success: false, error: 'Este e-mail já está cadastrado.' };
        }
        throw error;
      }

      const userData: VIPUser = {
        id: data.id,
        name: data.name,
        email: data.email,
        whatsapp: cleanWhatsapp || undefined,
        is_active: data.is_active,
      };

      setVipUser(userData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      return { success: true };
    } catch (err: any) {
      console.error('Erro no cadastro VIP:', err);
      return { success: false, error: err.message || 'Ocorreu um erro ao realizar o cadastro. Tente novamente.' };
    }
  };

  const logoutVIP = () => {
    setVipUser(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <VIPAuthContext.Provider value={{ vipUser, loading, loginVIP, registerVIP, logoutVIP }}>
      {children}
    </VIPAuthContext.Provider>
  );
};

export const useVIPAuth = () => {
  return useContext(VIPAuthContext);
};
